import React from 'react';
import {
  PrivacySection,
  PrivacyTitle,
  LastUpdated,
  SectionHeading,
  SectionText,
  List,
  ListItem,
} from '../styles/privacy';

function PrivacyPolicyPage() {
  return (
    <PrivacySection
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <PrivacyTitle>Privacy Policy</PrivacyTitle>
      <LastUpdated>Last updated: January 06, 2026</LastUpdated>

      <SectionHeading>1. Introduction</SectionHeading>
      <SectionText>
        Playfair ("we," "us," "our," or "Company") operates as a team fine management
        platform. This Privacy Policy explains how we collect, use, disclose, and
        safeguard your information when you visit our website and use our services.
      </SectionText>

      <SectionHeading>2. Information We Collect</SectionHeading>
      <SectionText>We collect information in the following ways:</SectionText>
      <List>
        <ListItem>
          <strong>Information You Provide:</strong> When you sign up for early access or
          contact us, we collect your email address and club name. You may also provide
          additional information through forms or correspondence.
        </ListItem>
        <ListItem>
          <strong>Automatic Information:</strong> When you visit our website, we
          automatically collect certain information about your device and browsing
          behavior, including IP address, browser type, operating system, and pages
          visited.
        </ListItem>
        <ListItem>
          <strong>Cookies:</strong> We use cookies and similar technologies to enhance
          your experience and understand how you use our website.
        </ListItem>
      </List>

      <SectionHeading>3. How We Use Your Information</SectionHeading>
      <SectionText>We use the information we collect to:</SectionText>
      <List>
        <ListItem>Manage your early access request and notify you when Playfair launches</ListItem>
        <ListItem>Respond to your inquiries and provide customer support</ListItem>
        <ListItem>Send you promotional materials and updates about our services (with your consent)</ListItem>
        <ListItem>Improve and optimize our website and services</ListItem>
        <ListItem>Comply with legal obligations</ListItem>
        <ListItem>Conduct research and analytics to understand user preferences</ListItem>
      </List>

      <SectionHeading>4. Information Sharing</SectionHeading>
      <SectionText>
        We do not sell, trade, or rent your personal information to third parties. We
        may share your information only in the following circumstances:
      </SectionText>
      <List>
        <ListItem>
          <strong>Service Providers:</strong> We may share information with third-party
          service providers who help us operate our website and conduct our business,
          such as hosting providers and analytics services.
        </ListItem>
        <ListItem>
          <strong>Legal Requirements:</strong> We may disclose your information when
          required by law or when we believe in good faith that such disclosure is
          necessary to protect our rights, your safety, or the safety of others.
        </ListItem>
        <ListItem>
          <strong>Business Transfers:</strong> If Playfair is involved in a merger,
          acquisition, or asset sale, your information may be transferred as part of that
          transaction.
        </ListItem>
      </List>

      <SectionHeading>5. Data Security</SectionHeading>
      <SectionText>
        We implement appropriate security measures to protect your personal information
        from unauthorized access, alteration, disclosure, or destruction. However, no
        method of transmission over the Internet is 100% secure, and we cannot guarantee
        absolute security.
      </SectionText>

      <SectionHeading>6. Data Retention</SectionHeading>
      <SectionText>
        We retain your personal information for as long as necessary to fulfill the
        purposes for which it was collected, including to satisfy legal, accounting, or
        reporting requirements. You may request deletion of your information at any time
        by contacting us.
      </SectionText>

      <SectionHeading>7. Your Rights</SectionHeading>
      <SectionText>
        Depending on your location, you may have certain rights regarding your personal
        information:
      </SectionText>
      <List>
        <ListItem>
          <strong>Right to Access:</strong> You have the right to request access to the
          personal information we hold about you.
        </ListItem>
        <ListItem>
          <strong>Right to Correction:</strong> You have the right to request that we
          correct any inaccurate or incomplete information.
        </ListItem>
        <ListItem>
          <strong>Right to Deletion:</strong> You have the right to request deletion of
          your personal information, subject to certain exceptions.
        </ListItem>
        <ListItem>
          <strong>Right to Opt-Out:</strong> You may opt out of receiving promotional
          communications from us at any time by following the unsubscribe instructions in
          our emails.
        </ListItem>
      </List>

      <SectionHeading>8. Third-Party Links</SectionHeading>
      <SectionText>
        Our website may contain links to third-party websites. This Privacy Policy
        applies only to information collected through our website. We are not responsible
        for the privacy practices of other websites. We encourage you to review the
        privacy policies of any third-party sites before providing your information.
      </SectionText>

      <SectionHeading>9. Children's Privacy</SectionHeading>
      <SectionText>
        Our website and services are not intended for children under the age of 13. We
        do not knowingly collect personal information from children under 13. If we
        become aware that a child under 13 has provided us with personal information, we
        will take steps to delete such information and terminate the child's account.
      </SectionText>

      <SectionHeading>10. Changes to This Privacy Policy</SectionHeading>
      <SectionText>
        We may update this Privacy Policy from time to time to reflect changes in our
        practices, technology, legal requirements, or other factors. We will notify you
        of any material changes by posting the updated Privacy Policy on our website and
        updating the "Last updated" date above.
      </SectionText>

      <SectionHeading>11. Contact Us</SectionHeading>
      <SectionText>
        If you have any questions about this Privacy Policy, or if you wish to exercise
        any of your rights, please contact us at:
        <br />
        <br />
        <strong>Playfair</strong>
        <br />
        Email: <a href="mailto:info@playfair.online">info@playfair.online</a>
        <br />
        Subject line: "Privacy Policy Inquiry"
      </SectionText>
    </PrivacySection>
  );
}

export default PrivacyPolicyPage;
